import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Quote, Building2, GraduationCap,
  Users, MapPin, CheckCircle2, Clock, PlayCircle, X, Play
} from 'lucide-react';

/* ─── Motion primitive ───────────────────────────────────────── */
const FadeUp = ({ children, className = '', delay = 0 }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 28 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-60px' }}
    transition={{ duration: 0.72, delay, ease: [0.25, 0, 0, 1] }}
  >
    {children}
  </motion.div>
);

/* ─── Data ───────────────────────────────────────────────────── */
const trialStats = [
  {
    value: '100%',
    label: 'Would use again',
    note: 'Every participant at Kamla Mehta School said they would use ClearWay again.',
  },
  {
    value: '9.7',
    label: 'Obstacle detection',
    note: 'Out of 10 — average confidence rating across the session.',
  },
  {
    value: '9.3',
    label: 'Feedback clarity',
    note: 'Out of 10 — sound and vibration rated clear and well-calibrated.',
  },
  {
    value: 'Zero',
    label: 'Learning curve',
    note: 'All participants understood the feedback immediately — no training required.',
  },
];

const trialContext = [
  {
    icon: <MapPin className="w-4 h-4" />,
    label: 'Location',
    value: 'Kamla Mehta School for the Blind, Mumbai',
  },
  {
    icon: <Clock className="w-4 h-4" />,
    label: 'Date',
    value: 'April 2026',
  },
  {
    icon: <Users className="w-4 h-4" />,
    label: 'Participants',
    value: 'Totally blind students and a teacher',
  },
  {
    icon: <Building2 className="w-4 h-4" />,
    label: 'Environments tested',
    value: 'School hallways, staircases, outdoor grounds',
  },
  {
    icon: <CheckCircle2 className="w-4 h-4" />,
    label: 'Key finding',
    value: 'Feedback understood immediately — zero training required',
  },
  {
    icon: <GraduationCap className="w-4 h-4" />,
    label: 'Prior mobility aid',
    value: 'White cane (and human assistance for some)',
  },
];

const quotes = [
  {
    quote: 'The sound feedback was clear and the GPS tracking was reliable. It was easy to use and helpful throughout.',
    name: 'Ashwini Srivastav',
    role: 'Teacher',
    institution: 'Kamla Mehta School for the Blind, Mumbai',
    isTeacher: true,
  },
  {
    quote: 'Walking with it felt natural. Being able to sense what was ahead — people, obstacles — gave me a new kind of confidence.',
    name: 'Anjali',
    role: 'Student',
    institution: 'Kamla Mehta School for the Blind',
    isTeacher: false,
  },
  {
    quote: "The spatial awareness it gave me was unlike anything I'd had before. This is going to give us real independence.",
    name: 'Komal',
    role: 'Student',
    institution: 'Kamla Mehta School for the Blind',
    isTeacher: false,
  },
];

const trialMedia = [
  {
    src: '/media/trial-1-kamla/explaining%20stick.jpg',
    alt: 'Founder Jaiveer Talwar explaining the ClearWay stick to the kids and teacher',
    caption: 'Founder Jaiveer Talwar explaining ClearWay to students and teachers',
    isFeatured: true, 
  },
  {
    src: '/media/trial-1-kamla/stick%20in%20hallway%20in%20use.jpg',
    alt: 'ClearWay in active use navigating the school hallway',
    caption: 'ClearWay in use — school hallway',
  },
  {
    src: '/media/trial-1-kamla/teacher%20with%20stick.jpg',
    alt: 'Ashwini Srivastav, teacher at Kamla Mehta School for the Blind, demonstrating ClearWay',
    caption: 'Ashwini Srivastav — teacher at the school',
  },
  {
    src: '/media/trial-1-kamla/stick%20vs%20cane%20teacher.jpg',
    alt: 'Teacher comparing ClearWay stick with a traditional white cane',
    caption: 'ClearWay compared with a traditional white cane',
  },
  {
    src: '/media/trial-1-kamla/school%20outside.jpg',
    alt: 'Outdoor trial grounds at Kamla Mehta School for the Blind, Mumbai',
    caption: 'Outdoor grounds — trial site',
  },
  {
    src: '/media/trial-1-kamla/group%20with%20stick.jpg',
    alt: 'Group of participants with the ClearWay stick',
    caption: 'Trial participants with ClearWay',
  },
];

const trialVideos = [
  {
    src: "https://videos.jtlr.org/v/1N01coTk5qxEODKjNvpdTaxkFGqs4n8W3QNoQfo9vvAA/embed",
    thumbnail: "/media/trial-1-kamla/stick in hallway in use.jpg", // Replace with actual thumbnail URL if needed
    title: "Navigating the hallway"
  },
  {
    src: "https://videos.jtlr.org/v/GN02GKUqXOMGPa00TQ2e7PTewk4KBoh2f5ep4opwZk7s00/embed",
    thumbnail: "/media/trial-1-kamla/teacher with stick.jpg", // Replace with actual thumbnail URL if needed
    title: "Teacher feedback"
  },
  {
    src: "https://videos.jtlr.org/v/qlg1Hu00ym1YCgWX02dRWmQXbziXH2K02sy19ZWbZBuaK8/embed",
    thumbnail: "/media/trial-1-kamla/school outside.jpg", // Replace with actual thumbnail URL if needed
    title: "Outdoor navigation"
  },
  {
    src: "https://videos.jtlr.org/v/5qm019MjRaWBMMiWpd7lxsAMsP8UtucYv4ceY2EgITvE/embed",
    thumbnail: "/media/trial-1-kamla/group with stick.jpg", // Replace with actual thumbnail URL if needed
    title: "Group session"
  }
];

/* ─── Page ───────────────────────────────────────────────────── */
const ImpactPage = () => {
  const [activeVideo, setActiveVideo] = useState(null);

  return (
    <div className="pt-24 text-white">

      {/* ══════════════════════════════════════════════════
          HERO — 3A
      ══════════════════════════════════════════════════ */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
              <CheckCircle2 className="w-4 h-4 text-blue-400" />
              <span className="text-blue-400 text-sm font-medium">Trial 1 — April 2026</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight mb-6">
              Tested in the real world.
              <br />
              <span className="text-gray-500">Proven by the people who matter most.</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
              In April 2026, ClearWay completed its first formal trial at Kamla Mehta School
              for the Blind, Mumbai. Totally blind students and a teacher navigated the school&apos;s
              hallways, staircases, and outdoor grounds — and the results were clear.
            </p>
          </motion.div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          STATS — 3B
      ══════════════════════════════════════════════════ */}
      <section className="py-16 px-6 bg-[#0f0f0f]">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {trialStats.map((stat, index) => (
              <FadeUp key={stat.label} delay={index * 0.1}>
                <div className="p-6 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50 text-center h-full flex flex-col">
                  <p className="text-4xl md:text-5xl font-bold text-white mb-2">{stat.value}</p>
                  <p className="text-blue-400 text-xs font-semibold uppercase tracking-widest mb-3">{stat.label}</p>
                  <p className="text-gray-500 text-sm leading-relaxed mt-auto">{stat.note}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          TRIAL CONTEXT
      ══════════════════════════════════════════════════ */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <FadeUp>
            <div className="p-8 md:p-12 rounded-3xl bg-gradient-to-br from-blue-500/5 to-purple-500/5 border border-gray-800/50">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-600 mb-8">Trial details</p>
              <div className="grid md:grid-cols-2 gap-6">
                {trialContext.map((item) => (
                  <div key={item.label} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400 flex-shrink-0 mt-0.5">
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.2em] text-gray-600 mb-0.5">{item.label}</p>
                      <p className="text-white text-sm font-medium leading-snug">{item.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </FadeUp>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          VIDEO DOCUMENTARY (THUMBNAILS + POPUP)
      ══════════════════════════════════════════════════ */}
      <section className="py-16 px-6 bg-[#0f0f0f]">
        <div className="max-w-6xl mx-auto">
          <FadeUp className="text-center mb-10">
            <div className="inline-flex items-center justify-center gap-2 mb-4 text-gray-600">
              <PlayCircle className="w-4 h-4" />
              <p className="text-[10px] uppercase tracking-[0.3em] mt-0.5">Watch the original video pitch</p>
            </div>
            <h2 className="text-3xl md:text-4xl font-semibold">Science fair presentation from 2021</h2>
          </FadeUp>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {trialVideos.map((video, index) => (
              <FadeUp key={index} delay={index * 0.1}>
                <div 
                  className="group relative w-full aspect-video rounded-3xl overflow-hidden border border-gray-800/60 bg-gray-900 shadow-xl cursor-pointer"
                  onClick={() => setActiveVideo(video.src)}
                >
                  <img 
                    src={video.thumbnail} 
                    alt={video.title} 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 opacity-60 group-hover:opacity-40"
                  />
                  <div className="absolute inset-0 flex flex-col items-center justify-center transition-all duration-300">
                    <div className="w-16 h-16 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center group-hover:scale-110 group-hover:bg-blue-500/80 transition-all duration-300">
                      <Play className="w-6 h-6 text-white ml-1" fill="currentColor" />
                    </div>
                    <p className="mt-4 text-white font-medium tracking-wide drop-shadow-md">{video.title}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          MEDIA
      ══════════════════════════════════════════════════ */}
      <section className="py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <FadeUp className="mb-8 text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-600">From the trial</p>
          </FadeUp>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {trialMedia.map((item, index) => (
              <FadeUp 
                key={item.src} 
                delay={index * 0.08}
                className={item.isFeatured ? "col-span-1 sm:col-span-2 md:col-span-3" : ""}
              >
                <div 
                  className={`group relative rounded-2xl overflow-hidden border border-gray-800/60 bg-gray-900 ${
                    item.isFeatured ? "aspect-video" : "aspect-[3/4]"
                  }`}
                >
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/70 to-transparent">
                    <p className="text-sm text-gray-200 font-medium">{item.caption}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
          <p className="text-center text-xs text-gray-600 mt-6">
            Photos used with consent. Under-18 participants are not identifiable in any image used here.
          </p>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          QUOTES — 3C
      ══════════════════════════════════════════════════ */}
      <section className="py-24 px-6 bg-[#0f0f0f]">
        <div className="max-w-5xl mx-auto">
          <FadeUp className="text-center mb-14">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gray-600 mb-3">In their own words</p>
            <h2 className="text-3xl md:text-4xl font-semibold">What participants said</h2>
          </FadeUp>

          <div className="grid md:grid-cols-3 gap-6">
            {quotes.map((q, index) => (
              <FadeUp key={q.name} delay={index * 0.1}>
                <div className={`h-full p-6 rounded-2xl border flex flex-col ${
                  q.isTeacher
                    ? 'bg-gradient-to-b from-blue-500/[0.06] to-transparent border-blue-500/20'
                    : 'bg-gradient-to-b from-gray-800/30 to-transparent border-gray-800/50'
                }`}>
                  <Quote className={`w-7 h-7 mb-4 flex-shrink-0 ${q.isTeacher ? 'text-blue-400/40' : 'text-gray-600'}`} />
                  <p className="text-gray-300 text-sm leading-relaxed flex-1 mb-6">
                    &ldquo;{q.quote}&rdquo;
                  </p>
                  <div className={`pt-4 border-t ${q.isTeacher ? 'border-blue-500/15' : 'border-gray-800/60'}`}>
                    <p className="font-semibold text-white text-sm">{q.name}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{q.role}</p>
                    <p className={`text-xs mt-0.5 ${q.isTeacher ? 'text-blue-400' : 'text-gray-500'}`}>{q.institution}</p>
                  </div>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          WHAT COMES NEXT — 3D
      ══════════════════════════════════════════════════ */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <FadeUp>
            <div className="text-center mb-10">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gray-600 mb-4">What comes next</p>
              <h2 className="text-3xl md:text-4xl font-semibold text-white">The work continues.</h2>
            </div>
            <p className="text-gray-400 leading-relaxed mb-5">
              Trial 1 confirmed that the core product works — in real environments, with real users,
              with no training required. The feedback was intuitive, the detection was trusted, and
              the experience moved people. That matters more than any number on a page.
            </p>
            <p className="text-gray-400 leading-relaxed mb-5">
              One improvement came back clearly across the session: participants want to use ClearWay
              alongside the folding white canes they already rely on. Making that possible — full
              compatibility with folding canes — is the next hardware milestone, and it&apos;s actively
              being developed.
            </p>
            <p className="text-gray-400 leading-relaxed">
              The next trial is being planned. The work continues.
            </p>
          </FadeUp>
        </div>
      </section>


      {/* ══════════════════════════════════════════════════
          CTA
      ══════════════════════════════════════════════════ */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-12 rounded-3xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-gray-800/50 text-center"
          >
            <h2 className="text-3xl md:text-4xl font-semibold mb-6">
              Follow the journey
            </h2>
            <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
              ClearWay is a student-led project building toward something real.
              If you&apos;re a school, organisation, or individual who wants to be involved, get in touch.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-medium hover:bg-gray-100 transition-all hover:scale-105"
              >
                Get in Touch
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/about"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-medium hover:bg-white/10 transition-all"
              >
                The Story
                <Building2 className="w-4 h-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          VIDEO MODAL POPUP
      ══════════════════════════════════════════════════ */}
      <AnimatePresence>
        {activeVideo && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-10 bg-black/95 backdrop-blur-sm"
            onClick={() => setActiveVideo(null)}
          >
            <button 
              className="absolute top-6 right-6 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
              onClick={() => setActiveVideo(null)}
            >
              <X className="w-6 h-6" />
            </button>
            
            <motion.div 
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-5xl aspect-video rounded-2xl overflow-hidden bg-black ring-1 ring-white/20 shadow-2xl"
              onClick={e => e.stopPropagation()} // Prevents click inside iframe from closing modal
            >
              <iframe 
                src={activeVideo} 
                title="ClearWay Trial Video"
                className="absolute inset-0 w-full h-full border-0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default ImpactPage;
